import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { MessageCircle, Search, Circle, Plus, X } from 'lucide-react';
import chatService from '../services/chatService';
import ChatWindow from './ChatWindow';
import './ChatDashboard.css';

const API_BASE_URL =
  process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:5000';

const ChatDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [socket, setSocket] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') || '');
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [allBuyers, setAllBuyers] = useState([]);
  const [buyerSearchQuery, setBuyerSearchQuery] = useState('');
  const [loadingBuyers, setLoadingBuyers] = useState(false);

  useEffect(() => {
    initializeSocket();
    loadRooms();

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (searchQuery) next.set('search', searchQuery); else next.delete('search');
      return next;
    }, { replace: true });
    loadRooms();
  }, [searchQuery, setSearchParams]);

  useEffect(() => {
    const requestedRoom = Number(searchParams.get('room'));
    if (requestedRoom && rooms.length && selectedRoom?.id !== requestedRoom) {
      const room = rooms.find((item) => item.id === requestedRoom);
      if (room) setSelectedRoom(room);
    }
  }, [rooms]);

  useEffect(() => {
    if (!showNewChatModal) return;

    const timeoutId = setTimeout(() => {
      loadAllBuyers(buyerSearchQuery);
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [showNewChatModal, buyerSearchQuery]);

  const initializeSocket = () => {
    const newSocket = io(API_BASE_URL, {
      transports: ['websocket'],
      reconnection: true
    });

    newSocket.on('connect', () => {
      console.log('Admin socket connected');

      // Authenticate as admin
      const adminData = JSON.parse(localStorage.getItem('adminUser') || '{}');
      newSocket.emit('authenticate', {
        userId: adminData.id,
        userType: 'admin'
      });
    });

    newSocket.on('new_message_notification', () => {
      loadRooms();
    });

    newSocket.on('user_online', ({ userId, userType }) => {
      if (userType === 'buyer') {
        setRooms(prev =>
          prev.map(room =>
            room.buyer_id === userId ? { ...room, isOnline: true } : room
          )
        );
      }
    });

    newSocket.on('user_offline', ({ userId, userType }) => {
      if (userType === 'buyer') {
        setRooms(prev =>
          prev.map(room =>
            room.buyer_id === userId ? { ...room, isOnline: false } : room
          )
        );
      }
    });

    setSocket(newSocket);

    // Request notification permission
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  };

  const loadRooms = async () => {
    try {
      setLoading(true);
      const params = {};

      if (searchQuery) params.search = searchQuery;

      const response = await chatService.getAllRooms(params);

      if (response.success) {
        setRooms(response.data.rooms);
      }
    } catch (error) {
      console.error('Load rooms error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoomSelect = (room) => {
    setSelectedRoom(room);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('room', String(room.id));
      return next;
    }, { replace: true });
  };

  const handleRoomUpdate = () => {
    loadRooms();
  };

  const loadAllBuyers = async (search = '') => {
    try {
      setLoadingBuyers(true);
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: '1',
        limit: '100'
      });
      if (search?.trim()) params.set('search', search.trim());

      const response = await fetch(`${API_BASE_URL}/api/admin/buyers?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();

      console.log('Buyers response:', data); // Debug log

      if (data.success) {
        // Set approved buyers - API returns data.buyers directly
        const buyers = data.data?.buyers || [];
        console.log('Loaded buyers:', buyers); // Debug log
        setAllBuyers(Array.isArray(buyers) ? buyers : []);
      } else {
        console.error('Failed to load buyers:', data.message);
        setAllBuyers([]);
      }
    } catch (error) {
      console.error('Load buyers error:', error);
      setAllBuyers([]);
    } finally {
      setLoadingBuyers(false);
    }
  };

  const handleStartNewChat = () => {
    setShowNewChatModal(true);
    setBuyerSearchQuery('');
    loadAllBuyers('');
  };

  const handleSelectBuyer = async (buyer) => {
    try {
      // Create or get existing room for this buyer
      const response = await chatService.createOrGetRoom(buyer.id);

      if (response.success) {
        setShowNewChatModal(false);
        setBuyerSearchQuery('');

        // Reload rooms to include the new one
        await loadRooms();

        // Select the new/existing room and persist it in the URL.
        const room = response.data.room;
        handleRoomSelect(room);
      }
    } catch (error) {
      console.error('Create room error:', error);
      alert('Failed to start chat. Please try again.');
    }
  };

  const getFilteredBuyers = () => {
    if (!buyerSearchQuery || buyerSearchQuery.trim() === '') return allBuyers;

    const query = buyerSearchQuery.toLowerCase().trim();
    return allBuyers.filter(buyer => {
      const businessName = buyer.business_name?.toLowerCase() || '';
      const contactPerson = buyer.contact_person?.toLowerCase() || '';
      const name = buyer.name?.toLowerCase() || '';
      const email = buyer.email?.toLowerCase() || '';
      const phone = buyer.phone?.toLowerCase() || '';
      const gst = buyer.gst_number?.toLowerCase() || '';

      return (
        businessName.includes(query) ||
        contactPerson.includes(query) ||
        name.includes(query) ||
        email.includes(query) ||
        phone.includes(query) ||
        gst.includes(query)
      );
    });
  };

  const totalUnread = rooms.reduce((sum, room) => sum + (room.unreadCount || 0), 0);

  return (
    <div className="chat-dashboard">
      {/* Sidebar */}
      <div className="chat-sidebar">
        {/* Header */}
        <div className="sidebar-header">
          <div className="sidebar-header-content">
            <h2>Support</h2>
            {totalUnread > 0 && (
              <span className="total-unread-badge">{totalUnread}</span>
            )}
          </div>
          <button className="new-chat-btn" onClick={handleStartNewChat} title="Start new chat">
            <Plus size={20} />
          </button>
        </div>

        {/* Simple inbox: no ticket analytics */}

        {/* Search */}
        <div className="search-container">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search buyers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        {/* Simple inbox: no active/unread/archived tabs */}

        {/* Rooms List */}
        <div className="rooms-list">
          {loading ? (
            <div className="loading-state">Loading conversations...</div>
          ) : rooms.length === 0 ? (
            <div className="empty-state">
              <p>No conversations found</p>
            </div>
          ) : (
            rooms.map(room => (
              <div
                key={room.id}
                className={`room-item ${selectedRoom?.id === room.id ? 'selected' : ''} ${room.unreadCount > 0 ? 'unread' : ''}`}
                onClick={() => handleRoomSelect(room)}
              >
                <div className="room-avatar">
                  <div className="avatar-circle">
                    {room.buyer?.business_name?.charAt(0) || 'B'}
                  </div>
                  {room.isOnline && <Circle size={10} fill="#10b981" color="#10b981" className="online-indicator" />}
                </div>

                <div className="room-info">
                  <div className="room-header">
                    <h4 className="room-name">{room.buyer?.business_name || 'Unknown'}</h4>
                    {room.lastMessage && room.lastMessage.created_at && (
                      <span className="room-time">
                        {new Date(room.lastMessage.created_at).toLocaleTimeString('en-US', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    )}
                  </div>

                  <div className="room-preview">
                    <p className="room-last-message">
                      {room.lastMessage?.message || 'No messages yet'}
                    </p>
                    {room.unreadCount > 0 && (
                      <span className="unread-badge">{room.unreadCount}</span>
                    )}
                  </div>

                  {room.tags && (
                    <div className="room-tags">
                      {room.tags.split(',').map((tag, index) => (
                        <span key={index} className="tag">{tag.trim()}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Window */}
      <div className="chat-main">
        {selectedRoom ? (
          <ChatWindow
            room={selectedRoom}
            socket={socket}
            onRoomUpdate={handleRoomUpdate}
          />
        ) : (
          <div className="no-chat-selected">
            <MessageCircle size={64} className="no-chat-icon" />
            <h3>Select a conversation</h3>
            <p>Choose a buyer from the list to start chatting</p>
          </div>
        )}
      </div>

      {/* New Chat Modal */}
      {showNewChatModal && (
        <div className="modal-overlay" onClick={() => setShowNewChatModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Start New Chat</h3>
              <button className="modal-close-btn" onClick={() => setShowNewChatModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="modal-search">
                <Search size={18} className="modal-search-icon" />
                <input
                  type="text"
                  placeholder="Search buyers..."
                  value={buyerSearchQuery}
                  onChange={(e) => setBuyerSearchQuery(e.target.value)}
                  className="modal-search-input"
                  autoFocus
                />
              </div>

              <div className="buyers-list">
                {loadingBuyers ? (
                  <div className="modal-loading">Loading buyers...</div>
                ) : getFilteredBuyers().length === 0 ? (
                  <div className="modal-empty">
                    {buyerSearchQuery ? 'No buyers found' : 'No buyers available'}
                  </div>
                ) : (
                  getFilteredBuyers().map(buyer => (
                    <div
                      key={buyer.id}
                      className="buyer-item"
                      onClick={() => handleSelectBuyer(buyer)}
                    >
                      <div className="buyer-avatar">
                        {buyer.business_name?.charAt(0) || 'B'}
                      </div>
                      <div className="buyer-details">
                        <div className="buyer-name-row">
                          <div className="buyer-name">{buyer.business_name}</div>
                          <span className={`approval-badge ${buyer.approval_status || 'unknown'}`}>
                            {(buyer.approval_status || 'unknown').toUpperCase()}
                          </span>
                        </div>
                        <div className="buyer-email">{buyer.email}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatDashboard;
